/* eslint-disable camelcase */

import qs from 'qs';

import { renderZbpLabelPrintModal, renderHubLabelPrintModal } from 'label/LabelPrint';
import store from 'pages/store';
import { PRINTER_FORMATS, PRINTER_STATUSES } from 'pages/views/devices/socketClient/types';
import { setHubPrintLoading, setHubPrintLoadingError } from 'root/apps/Hub/Receipt';
import socketClient from 'root/apps/pages/views/devices/socketClient';
import { PrintEvents } from 'root/tracking.types';

// @NOTE careful, this ID is used by Golf
const printHolder = 'print_holder';
const receiptPreviewContainer = 'receipt_preview_container';
export const receiptPreviewFrame = 'receipt_preview_iframe';
const reportPreviewFrame = 'report_preview_iframe';
export const goodPrinterStatusArray = [
  PRINTER_STATUSES.READY,
  PRINTER_STATUSES.LOW_PAPER,
  PRINTER_STATUSES.NOT_AVAILABLE,
];

/**
 * This class handles printing functions
 */
export class Print implements MerchantosPrint {
  /**
   * Computes into a boolean if the user is on Stencil and if printLabel is enabled
   */
  hasStencilPrintLabelsMethod = () => {
    let hasMethods = true;

    if (
      !window.isGriffin() ||
      !window.webkit ||
      !window.webkit.messageHandlers ||
      !window.webkit.messageHandlers.stencilPrintLabels ||
      typeof window.webkit.messageHandlers.stencilPrintLabels.postMessage !== 'function'
    ) {
      hasMethods = false;
    }

    return hasMethods;
  };

  /**
   * A callback to signal that printing has finished.
   * @param {string} error
   */
  printDone = (error?: string) => {
    window.hideNotifier('working');
    if (error) {
      window.flashNotifier('warning', error, 15);
    }
  };

  /**
   * Handles print requests from within the Print Templates.
   * @param {('label' | 'receipt' | 'report')} pageType "receipt", "label" or "report"
   * @returns {boolean}
   */
  print = (pageType: PageType) => {
    const url = window.location.href;
    // TODO: Handle cases here and in other places for label printing

    if (window.isGriffin() && pageType === 'receipt') {
      window.flashNotifier('working', 'Printing', 4);
      const receiptInfo = window.StencilWebAPI.printer.printJob(url, false);
      window.StencilWebAPI.printer.print(receiptInfo);
    } else {
      window.print();
    }
    return true;
  };

  /**
   * Determine print flows for different pageTypes
   * @param {Object} printOptions Print job configurations
   * @param {object} printOptions.el Clicked element
   * @param {string} printOptions.url Api url to be printed
   * @param {string} printOptions.target "_blank"
   * @param {('label' | 'receipt' | 'report')} printOptions.pageType "receipt", "label" or "report"
   * @param {boolean} printOptions.openCashDrawer Whether or not to open the cash drawer
   * @param {boolean} printOptions.isHubPrintable Cherry-pick "report" types to be sent to the Hub
   * @param {boolean} printOptions.forcePrint Only used on register receipts
   * @returns {void}
   */
  printUrl = ({
    el,
    url,
    target = '_blank',
    pageType = 'report',
    openCashDrawer = false,
    isHubPrintable = false,
    forcePrint = false,
  }: MerchantosPrintParams) => {
    if (window.isGriffin()) {
      switch (pageType) {
        case 'label': {
          this.sendStencilPrintLabelMessage(url);
          break;
        }
        case 'report': {
          this.printStencilReport(url);
          break;
        }
        case 'receipt': {
          if (this.isPrintHolderVisible()) {
            this.createPrintPreviewContainer();
            this.createPrintPreviewFrame({ url });
          }
          window.flashNotifier('working', 'Printing');
          const receiptInfo = window.StencilWebAPI.printer.printJob(url, openCashDrawer);
          window.StencilWebAPI.printer.print(receiptInfo);
          break;
        }
        default:
        // swallow
      }
    } else if (
      (pageType === 'receipt' || isHubPrintable) &&
      // Printing will occur in the same window if the #print_holder is visible,
      // if it's a register receipt or if it's a Hub print.
      // @NOTE this will also be enabled in a future PR behind a module for Kiosk POS
      (this.isPrintHolderVisible() ||
        forcePrint ||
        socketClient.getHubPrinterStatus(socketClient.PRINTER_KINDS.RECEIPT))
    ) {
      this.handlePrintReceipt({ el, url, openCashDrawer, forcePrint, isHubPrintable });
    } else {
      if (['receipt', 'label'].includes(pageType)) {
        // We can only assume the print will be completed, at least we can get a
        // pulse of how many time the code enters this flow
        window.dataLayer.push({
          event: PrintEvents.BROWSER,
          kind: pageType.toUpperCase(),
        });
      }

      window.open(url, target);
    }
  };

  /**
   * Asserts whether the "printHolder" container is visible
   * @returns {boolean} isVisible
   */
  isPrintHolderVisible = () => $(`#${printHolder}`).is(':visible');

  // @ts-expect-error ts-migrate(2416) FIXME: Property 'getReceiptPreviewContainer' in type 'Pri... Remove this comment to see the full error message
  getReceiptPreviewContainer = () =>
    this.isPrintHolderVisible()
      ? document.getElementById(printHolder)
      : document.getElementById(receiptPreviewContainer);

  /**
   * Creates the iframe inside the print preview container
   * @param {Object} printOptions Print job configurations
   * @param {string} printOptions.url - Receipt/Quote API url
   */
  createPrintPreviewFrame = ({ url, forcePrint }: { url: string; forcePrint?: boolean }) => {
    if (document.getElementById(receiptPreviewFrame)) {
      document.getElementById(receiptPreviewFrame)!.remove();
    }

    const iframe = document.createElement('iframe');

    Object.assign(iframe, {
      id: receiptPreviewFrame,
      src: url,
      width: '100%',
      height: '400px',
      onload: forcePrint
        ? () => {
            iframe.contentWindow!.print();
          }
        : undefined,
    });

    Object.assign(iframe.style, {
      maxWidth: '600px',
      border: '1px solid #888',
      padding: '10px',
      margin: '20px',
    });
    // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
    this.getReceiptPreviewContainer().appendChild(iframe);
  };

  /**
   * Creates or resets the print preview container for the iframe to be added inside
   * @returns {void}
   */
  createPrintPreviewContainer = () => {
    const previewContainer = this.getReceiptPreviewContainer();

    if (!previewContainer) {
      const previewContainer = document.createElement('div');

      Object.assign(previewContainer, {
        id: receiptPreviewContainer,
      });

      Object.assign(previewContainer.style, {
        // In order to have the media queries in the template executed properly
        // the container needs to be rendered and not simply display: none
        visibility: 'hidden',
        position: 'fixed',
        width: '100%',
        top: 0,
        left: 0,
        'z-index': -1,
      });

      // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
      document.getElementById('wrapper').appendChild(previewContainer);
    } else {
      previewContainer.innerHTML = '';
    }
  };

  /**
   * Triggers a print first attempting via Hub app and then deferring to the default template print.
   * @param {Object} printOptions Print job configurations
   * @param {object} printOptions.el Clicked element
   * @param {string} printOptions.url Api url of the receipt
   * @param {boolean} printOptions.openCashDrawer Whether or not to open the cash drawer
   * @param {boolean} printOptions.forcePrint Only used on register receipts
   */
  handlePrintReceipt = ({
    el,
    url,
    openCashDrawer,
    forcePrint,
  }: Omit<MerchantosPrintParams, 'target' | 'pageType'>) => {
    const previewIframe = document.getElementById(receiptPreviewFrame) as HTMLIFrameElement;

    this.createPrintPreviewContainer();
    this.createPrintPreviewFrame({ url, forcePrint });

    // Ignore no_auto_print if forcePrint is true
    const shouldPrint = !this.getPrintUrlParams(url).no_auto_print || forcePrint;

    if (socketClient.getHubPrinterStatus(socketClient.PRINTER_KINDS.RECEIPT) && shouldPrint) {
      this.hubPrintReceipt({ el, url, openCashDrawer });
    } else if (forcePrint) {
      previewIframe.contentWindow!.print();
    }
  };

  private getPrintUrlParams = (url: string): { no_auto_print: boolean } => {
    const params = qs.parse(url);

    return { no_auto_print: params.no_auto_print === '1' };
  };

  /**
   * Attempts to request a print job using the Hub application. If it fails it
   * defers to the print template's original print function
   * @param {Object} printOptions Print job configurations
   * @param {object} printOptions.el Clicked element
   * @param {boolean} printOptions.url Api url of the receipt
   * @param {boolean} printOptions.openCashDrawer Whether or not to open the cash drawer with this print job
   * @returns {void}
   */
  hubPrintReceipt = ({
    el,
    url,
    openCashDrawer,
    timeout = 3000,
  }: Pick<MerchantosPrintParams, 'el' | 'url' | 'openCashDrawer'> & { timeout?: number }) => {
    setHubPrintLoading({ el, url });

    const executionTimes: { [key: string]: number } = {
      RETAIL_PRINT_START: Date.now(),
    };
    const iframe = document.getElementById(receiptPreviewFrame) as HTMLIFrameElement;
    const originalPrint = iframe.contentWindow!.print;
    (iframe.contentWindow! as Window & { originalPrint: () => void }).originalPrint = originalPrint;

    let isPrinted = false;
    let hasInlinePrintCommand = false;
    let inlinePrintTimeout: TSFixMe = null;

    iframe.contentWindow!.print = () => {
      executionTimes.RETAIL_RECEIPT_LOADED = Date.now();
      try {
        // @NOTE >Temporary solution< delete this hack when we get rid of custom templates
        // If a custom template has a window.print() in it, the print job would be sent twice.
        // To support this "feature" and only it once, the first print will be blocked.
        if (
          !hasInlinePrintCommand &&
          iframe.contentWindow!.document.documentElement.outerHTML.includes('window.print()')
        ) {
          hasInlinePrintCommand = true;
          // Arbitrary 3s timeout, if the template fails/hangs printing from
          // its custom code it will print anyway, too bad
          inlinePrintTimeout = setTimeout(iframe.contentWindow!.print, timeout);
          return;
        }
        clearTimeout(inlinePrintTimeout);
        if (isPrinted) return;
        isPrinted = true;

        this.hubPrintHtml({
          html: iframe.contentWindow!.document.documentElement.outerHTML,
          openCashDrawer,
          executionTimes,
        });
      } catch (err) {
        window.newrelic && window.newrelic.noticeError(err);
        // Attempt a print anyway with browser printing if we fail to print it with the Hub
        originalPrint();
      }
    };
  };

  hubPrintHtml = ({
    html,
    openCashDrawer = false,
    executionTimes,
  }: {
    html: string;
    openCashDrawer?: boolean;
    executionTimes: { [key: string]: number };
  }) => {
    const cleanedHtml = html
      .replace(/<!--[\s\S]+?-->/g, '')
      // Remove the script tags
      .replace(/<script[\s\S]+?<\/script>/g, '')
      .replace(/\s+/g, ' ')
      // Remove the link tags
      .replace(/<link[\s\S]+?>/g, '')
      // Remove the style spaces
      .replace(/<style[\s\S]+?<\/style>/g, style => style.replace(/\s*([:;{},])\s*/g, '$1'))
      // Reset the document padding and margins in case they were modified in a custom template
      .replace(
        /<style>/,
        '<style>body{padding:0!important;margin:0!important;}.transfer{margin:0!important}'
      )
      // Remove space between html tags
      .replace(/\s*(<[^>]+>)\s*/g, '$1');
    executionTimes.RETAIL_HTML_TRANSFORMED = Date.now();

    const kind = socketClient.PRINTER_KINDS.RECEIPT;
    const printerStatus = socketClient.getHubPrinterStatus(kind);
    if (goodPrinterStatusArray.includes(printerStatus)) {
      socketClient.send(socketClient.PRINTER_MESSAGES.PRINT, {
        kind,
        data: cleanedHtml,
        format: PRINTER_FORMATS.HTML,
        executionTimes,
        openCashDrawer,
      });
    } else {
      setHubPrintLoadingError({ kind });
    }
  };

  /**
   * Determine if there was a credit card charge for CLDPAD receipt printing
   * @param {array} payments SalePayment objects
   */
  isCreditCardCharge = (payments: TSFixMe) => {
    let charge = false;
    if (Array.isArray(payments)) {
      payments.forEach(payment => {
        if (payment.ccChargeID > 0) {
          charge = true;
        }
      });
    } else if (payments.ccChargeID > 0) {
      charge = true;
    }
    return charge;
  };

  /**
   * Return payments for a sale that is being printed
   * @param {string} url to be printed
   */
  getPayments = (url: string) => {
    // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
    const saleID = url.match(/\/(\d+)\.html/)[1];
    return window.lsCloud.getJSONFromAPI(
      `/API/Account/${
        // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
        window.merchantos.session.sessData.systemCustomerID
      }/DisplayTemplate/Sale/${saleID}`
    );
  };

  /**
   * Open the cash drawer by printing a receipt
   * @param {string} url of the receipt to print to open the drawer
   */
  openCashDrawer = (url: string) => {
    if (window.isGriffin()) {
      window.StencilWebAPI.printer.openDrawer();
    } else {
      this.handlePrintReceipt({ url, openCashDrawer: true });
    }
  };

  handlePrintLabels = async ({
    target,
    url,
    isZbpEnabled,
  }: {
    url: string | undefined;
    target: string;
    isZbpEnabled: boolean;
  }) => {
    const labelUrl = url || (await this.printLabels(false));

    const hasHubLabelPrinter = store.getState().hub.defaultPrinters.LABEL;

    if (!isZbpEnabled || window.isGriffin()) {
      this.printUrl({ url: labelUrl, pageType: 'label', target });
      return;
    }

    if (hasHubLabelPrinter) {
      renderHubLabelPrintModal(labelUrl);
      return;
    }
    renderZbpLabelPrintModal(labelUrl);
  };

  /**
   * This is used by the item labels listing to print all labels.
   * The print function doesn't otherwise update when the search params change
   * @param {Boolean | string} printUrl - Send the url to the print command
   * @return {Deferred}
   */
  printLabels = (printUrl: boolean | string) => {
    const params: { [key: string]: any } = {};
    const searchParams = window.merchantos.utils.getQueryParams(printUrl);
    const employee_id = searchParams.employee_id;
    const hide_pos = searchParams.hide_pos;
    const start_date = searchParams.start_date;
    const end_date = searchParams.end_date;
    const deferred = $.Deferred();

    if (employee_id && Number(employee_id) > 0) {
      params.employeeID = employee_id;
    } else if (typeof employee_id === 'undefined') {
      params.employeeID = $('#listing_employee_id').val();
    }
    if (hide_pos && hide_pos === 'on') {
      params.orderID = 0;
    }
    if ((start_date && start_date !== '00/00/0000') || (end_date && end_date !== '00/00/0000')) {
      let createTime = '';
      if (!end_date) {
        createTime = `>=,${start_date} 00:00:00`;
      } else if (!start_date) {
        createTime = `<=,${end_date} 23:59:59`;
      } else {
        createTime = `><,${start_date} 00:00:00,`;
        createTime += `${end_date} 23:59:59`;
      }
      params.createTime = createTime;
    }
    params.template = 'ItemLabel';
    params.print = '1';

    window.lsCloud.Options.get('label_size', { reload: true }).done(size => {
      params.labelSize = size;
      // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
      params.shopID = window.merchantos.session.getShop().shopID;

      const query = window.merchantos.utils.getQueryString(params);
      let url = `/API/Account/${window.merchantos.session.getSystemCustomerID()}`;
      url += `/DisplayTemplate/Label.html${query}`;

      if (printUrl) {
        window.merchantos.print.printUrl({ url, pageType: 'label' });
      }

      deferred.resolve(url);
    });

    return deferred;
  };

  /**
   * Show print label buttons if the printLabel method is enabled on Stencil
   */
  showPrintLabelButtonsOnStencil = () => {
    if (this.hasStencilPrintLabelsMethod()) {
      $('.griffin-no-display.print-label').removeClass('griffin-no-display');
    }
  };

  /**
   * When a print command is sent, evaluate if we are able to printLabel on Stencil
   * @param {string} url Api url to print
   * @returns {boolean} is or not able to printLabels
   */
  sendStencilPrintLabelMessage = (url: string) => {
    if (
      this.hasStencilPrintLabelsMethod() &&
      /API\/Account\/\d+\/DisplayTemplate.+?template=ItemLabel/.test(url)
    ) {
      window.webkit.messageHandlers.stencilPrintLabels.postMessage({
        labelPath: url.replace(/\.html/, '.json'),
      });
    }
  };

  // From the HTML viewer on iPad it is no possible to print the content of an iframe
  // only the current page will be able to be printed, therefore the iframe is set in the
  // background and the above containers are set to not be printed using media queries
  printStencilReport = (url: string) => {
    const eventName = 'ontouchstart' in document.documentElement ? 'touchstart' : 'click';
    const removeFrame = () => {
      // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
      document.getElementById('wrapper').classList.remove('no_print');
      // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
      document.getElementById('wrapper').style.background = '';
      // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
      document.getElementById(reportPreviewFrame).remove();
      window.removeEventListener(eventName, removeFrame);
    };

    if (document.getElementById(reportPreviewFrame)) {
      removeFrame();
    }

    const iframe = document.createElement('iframe');
    iframe.onload = () => {
      // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
      iframe.height = `${iframe.contentWindow.document.body.scrollHeight}px`;
      iframe.className = 'no_screen';

      window.focus();
      window.print();

      window.addEventListener(eventName, removeFrame);
    };

    iframe.style.position = 'absolute';
    iframe.style.top = '0';
    iframe.style.width = '100%';
    iframe.style.border = '0';
    iframe.style.zIndex = '-1';
    iframe.id = reportPreviewFrame;
    iframe.src = url;
    document.body.appendChild(iframe);

    // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
    document.getElementById('wrapper').classList.add('no_print');
    // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
    document.getElementById('wrapper').style.background = '#FFF';
  };

  /**
   * Print a transaction receipt for refund transactions
   * @param {number} transactionId
   */
  printTransactionReceipt = (transactionId: TSFixMe) => {
    const url = `/API/Account/${window.merchantos.session.getSystemCustomerID()}/DisplayTemplate/Sale/${transactionId}.html?template=SaleReceipt&print=1&page_width=auto&page_height=2000mm&job_type=receipt`;

    this.printUrl({ url, pageType: 'receipt' });
  };

  hubPrintByFrameId = ({ el, id }: { el?: HTMLElement; id: string }) => {
    const iframe = document.getElementById(id) as HTMLIFrameElement;

    if (socketClient.getHubPrinterStatus(socketClient.PRINTER_KINDS.RECEIPT)) {
      setHubPrintLoading({ el });

      const executionTimes = {
        RETAIL_PRINT_START: Date.now(),
      };

      this.hubPrintHtml({
        // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
        html: iframe.contentWindow.document.documentElement.outerHTML,
        executionTimes,
      });
    } else {
      // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
      iframe.contentWindow.print();
    }
  };
}

// @ts-expect-error ts-migrate(2322) FIXME: Type 'Print' is not assignable to type 'Merchantos... Remove this comment to see the full error message
window.merchantos.print = new Print();
